// Privacy Policy v2.0 — Effective May 8, 2026
// Source: HomeCure_Privacy_Policy_v2-1.pdf (provided by user)
// Operated by MESRON HEALTHCARE LLP (LLPIN: ACD-8580)

export const PRIVACY_HTML = `
<p><strong>HomeCure</strong> | by MESRON HEALTHCARE LLP<br/>
Privacy Policy v2.0 | Effective: May 8, 2026 | Last Updated: June 2026<br/>
Governing Law: Laws of the Republic of India | Jurisdiction: Courts of West Bengal, India<br/>
Language: English (Controlling Version)</p>

<p><strong>IMPORTANT NOTICE:</strong> This Privacy Policy is a legally binding agreement between You (the User, Hospital, or Healthcare Personnel) and MESRON HEALTHCARE LLP, operating the HomeCure platform. By registering on or using HomeCure, You expressly consent to the collection, storage, processing, and sharing of Your personal data as described herein. This Policy applies to three distinct categories of users: (1) Patients/Users, (2) Hospitals, and (3) Healthcare Personnel. If You do not agree to this Policy, discontinue use immediately.</p>

<p>admin@homecure.co.in &nbsp;|&nbsp; +91-9002296906 &nbsp;|&nbsp; Grievance Officer: Dr. Indranil Chatterjee</p>

<hr/>

<h1>Table of Contents</h1>
<p><strong>PART A — General Provisions (All Users):</strong> 1. Definitions • 2. About MESRON HEALTHCARE LLP and HomeCure • 3. Scope, Applicability and Three-Tier User Structure • 4. Legal Basis and Applicable Laws • 5. Methods of Data Collection • 6. Data Localisation and Cross-Border Transfer • 7. Data Retention and Deletion • 8. Security Practices and Safeguards • 9. Data Sharing and Third-Party Disclosure • 10. Cookies, Device Tracking and Push Notifications • 11. Grievance Redressal Mechanism • 12. Amendments.</p>
<p><strong>PART B — Patients / Users:</strong> 13. Patient Data • 14. Health Profile and SPDI • 15. Hospital Bed Bookings — SOS and Scheduled • 16. Home Healthcare Services • 17. Telemedicine, Video and Masked Audio Calling • 18. Health Records Storage • 19. Health Insurance Data • 20. Reviews and Ratings • 21. Payment Processing • 22. Consent for Sharing with Hospitals • 23. Minor Users • 24. Rights of the Data Principal.</p>
<p><strong>PART C — Hospitals:</strong> 25. Registration and Verification • 26. Booking and Bed Management • 27. Personnel Data under Hospitals • 28. Commission and Payment • 29. Patient Data Sharing Conditions • 30. Review Data.</p>
<p><strong>PART D — Healthcare Personnel:</strong> 31. Registration and Verification • 32. Home Healthcare Personnel — Booking Data • 33. Hospital-listed Personnel • 34. Video and Masked Audio Calling • 35. Commission, Payment and Financial Data • 36. Review Data.</p>
<p><strong>PART E — Intermediary Status:</strong> 37. Intermediary Status • 38. Hospital Billing Disclaimer • 39. Limitation of Liability • 40. Contact Information.</p>

<hr/>

<h1>PART A — GENERAL PROVISIONS</h1>

<h2>1. Definitions and Interpretation</h2>
<ul>
<li><strong>"Platform"</strong>: The HomeCure mobile application (iOS and Android), website (www.homecure.co.in), and all associated digital interfaces operated by MESRON HEALTHCARE LLP.</li>
<li><strong>"MESRON HEALTHCARE LLP" / "We" / "Us" / "Our" / "Company"</strong>: MESRON HEALTHCARE LLP (LLPIN: ACD-8580), an LLP registered under the LLP Act, 2008, having registered office at Premises No.159/63, Street No.2, Hindusthan Park, Hirapur, Asansol — 713304, West Bengal, India.</li>
<li><strong>"HomeCure"</strong>: The brand name and digital platform operated by MESRON HEALTHCARE LLP at www.homecure.co.in.</li>
<li><strong>"User" / "Patient" / "Data Principal"</strong>: Any natural person registering as a patient/service-seeker on the Platform.</li>
<li><strong>"Hospital"</strong>: Any registered clinical establishment, nursing home, hospital, or diagnostic centre that has onboarded the Platform as a service provider after successful verification.</li>
<li><strong>"Healthcare Personnel"</strong>: Doctors, nurses, physiotherapists, dieticians, medical technologists, and Ayahs (attendants) registered on the Platform.</li>
<li><strong>"Home Healthcare Personnel"</strong>: Healthcare Personnel registered directly under HomeCure for providing services at patients' premises.</li>
<li><strong>"Hospital Healthcare Personnel"</strong>: Doctors, physiotherapists, and dieticians listed on the Platform under a verified Hospital.</li>
<li><strong>"Personal Data"</strong>: Any data about an individual who is identifiable by or in relation to such data, as defined under the DPDP Act, 2023.</li>
<li><strong>"SPDI"</strong>: Sensitive Personal Data or Information under Rule 3 of the SPDI Rules, 2011, including health records, financial data, passwords and biometric data.</li>
<li><strong>"Data Fiduciary"</strong>: MESRON HEALTHCARE LLP, determining the purpose and means of processing personal data under the DPDP Act, 2023.</li>
<li><strong>"SOS Booking"</strong>: An urgent / emergency bed or service booking made by a Patient for immediate hospital admission or healthcare assistance.</li>
<li><strong>"Scheduled Booking"</strong>: A pre-planned booking for a hospital bed, in-person consultation, video consultation, or home healthcare visit.</li>
<li><strong>"Commission"</strong>: The fee charged by MESRON HEALTHCARE LLP from Patients for facilitating bookings through the Platform.</li>
<li><strong>"Video Consultation"</strong>: A remote medical consultation via encrypted video call.</li>
<li><strong>"Masked Audio Call"</strong>: A telephonic communication where both parties' actual mobile numbers are concealed via a virtual proxy number system.</li>
<li><strong>"Health Record"</strong>: Prescriptions, medical reports, diagnostic scans, discharge summaries, or any other health document stored by the User on the Platform.</li>
<li><strong>"Minor"</strong>: Any individual below the age of eighteen (18) years.</li>
<li><strong>"Consent"</strong>: Free, specific, informed, unconditional and unambiguous indication of the Data Principal's agreement as mandated under Section 6 of the DPDP Act, 2023.</li>
</ul>

<h2>2. About MESRON HEALTHCARE LLP and the HomeCure Platform</h2>
<p>MESRON HEALTHCARE LLP (LLPIN: ACD-8580), operating under the brand name HomeCure, is a technology-driven intermediary healthcare platform. HomeCure operates as a digital marketplace and intermediary connecting Patients with Hospitals and Healthcare Personnel for home healthcare services, telemedicine consultations, and hospital bed bookings.</p>
<p>HomeCure has been operating home healthcare services on a pilot basis (offline) in Asansol, Durgapur, and Bardhaman in West Bengal, India, for the past two years. The full digital platform is currently under development.</p>
<p>The Platform operates across three distinct tiers — <strong>Patients/Users</strong>, <strong>Hospitals</strong>, and <strong>Healthcare Personnel</strong>. MESRON HEALTHCARE LLP acts as an intermediary platform within the meaning of Section 2(1)(w) of the Information Technology Act, 2000, and charges a commission from Patients for bookings facilitated through the Platform. MESRON HEALTHCARE LLP does not directly provide medical services and is not a clinical establishment under the Clinical Establishments Act, 2010.</p>

<h2>3. Scope, Applicability and Three-Tier User Structure</h2>
<p>This Privacy Policy applies to all three tiers — Patients, Hospitals, and Healthcare Personnel — and covers all personal data processed in connection with use of the Platform.</p>
<h3>3.1 Territorial Scope</h3>
<p>HomeCure services are presently offered exclusively within the territory of India. All personal data is stored on servers located within India (AWS India / equivalent Indian cloud infrastructure).</p>
<h3>3.2 Exclusions</h3>
<p>This Policy does not govern the independent privacy practices of Hospitals, third-party payment gateways, or individual Healthcare Personnel beyond their obligations under their agreements with MESRON HEALTHCARE LLP.</p>

<h2>4. Legal Basis and Applicable Laws</h2>
<ul>
<li><strong>Digital Personal Data Protection Act, 2023 (DPDP Act)</strong> — India's primary data protection legislation.</li>
<li><strong>DPDP Rules, 2025</strong> — Rules notified by MeitY on 13 November 2025.</li>
<li><strong>IT Act, 2000 — Sections 43A, 72A</strong>.</li>
<li><strong>SPDI Rules, 2011</strong>.</li>
<li><strong>IT (Intermediary Guidelines and Digital Media Ethics Code) Rules, 2021</strong>.</li>
<li><strong>Telemedicine Practice Guidelines, 2020</strong>.</li>
<li><strong>Consumer Protection Act, 2019</strong> and E-Commerce Rules, 2020.</li>
<li><strong>Payment and Settlement Systems Act, 2007 / RBI Guidelines</strong>.</li>
<li><strong>Clinical Establishments (Registration and Regulation) Act, 2010</strong>.</li>
<li><strong>ABDM / NDHM Health Data Management Policy</strong>.</li>
<li><strong>Bharatiya Nagarik Suraksha Sanhita, 2023 (BNSS)</strong>.</li>
</ul>

<h2>5. Methods of Data Collection</h2>
<ul>
<li><strong>Direct Collection</strong>: Registration; booking; health profile setup; document uploads; reviews; support communications.</li>
<li><strong>Automated Collection</strong>: Cookies; device metadata; GPS location (with consent); anonymised app usage analytics; push notification interaction data.</li>
<li><strong>Third-Party Sources</strong>: Healthcare Personnel clinical notes post-service (with patient consent); Hospital partners; payment gateway partners; verification authorities.</li>
</ul>
<p>MESRON HEALTHCARE LLP does not collect data from social media platforms without explicit consent. No data scraping or covert collection methods are employed.</p>

<h2>6. Data Localisation and Cross-Border Transfer</h2>
<p>All personal data is stored exclusively on servers physically located within India. MESRON HEALTHCARE LLP does not currently transfer personal data outside India. Any future cross-border transfer will be conducted only after obtaining explicit User consent and complying with Section 16 of the DPDP Act, 2023.</p>

<h2>7. Data Retention and Deletion</h2>
<table>
<thead><tr><th>Data Category</th><th>Retention Period</th><th>Basis</th></tr></thead>
<tbody>
<tr><td>Active user / hospital / personnel account data</td><td>Duration of account + 3 years post-closure</td><td>Legitimate interest + Legal obligation</td></tr>
<tr><td>Health records and medical documents</td><td>7 years (min. 3 years post last service)</td><td>Medical Records Rules; SPDI Rules</td></tr>
<tr><td>SOS and emergency booking records</td><td>3 years minimum</td><td>Medicolegal liability</td></tr>
<tr><td>Hospital bed booking and commission records</td><td>7 years</td><td>Companies Act 2013; GST Act</td></tr>
<tr><td>In-person and video consultation records</td><td>7 years</td><td>Telemedicine Guidelines</td></tr>
<tr><td>Masked audio call metadata (not content)</td><td>90 days</td><td>Billing and dispute resolution</td></tr>
<tr><td>Payment and financial records</td><td>7 years</td><td>Companies Act 2013; GST; RBI</td></tr>
<tr><td>Personnel verification documents</td><td>Duration of account + 5 years</td><td>Regulatory compliance</td></tr>
<tr><td>Hospital registration documents</td><td>Duration of account + 5 years</td><td>Regulatory compliance</td></tr>
<tr><td>Review and rating data</td><td>Platform operation or deletion request</td><td>Legitimate interest</td></tr>
<tr><td>Cookie data (analytical)</td><td>Max 13 months</td><td>IT Rules; DPDP Act</td></tr>
<tr><td>Grievance and complaint records</td><td>3 years</td><td>Legal obligation</td></tr>
</tbody>
</table>
<p>Upon expiry of the retention period, data is securely and irreversibly deleted or anonymised. Users may request early deletion subject to overriding legal retention obligations.</p>

<h2>8. Security Practices and Safeguards</h2>
<h3>8.1 Technical Safeguards</h3>
<ul>
<li>AES-256 encryption for all data at rest across HomeCure servers;</li>
<li>TLS 1.2 / TLS 1.3 for all data in transit;</li>
<li>End-to-end encryption for video consultation sessions;</li>
<li>Virtual proxy number masking for all audio calls;</li>
<li>Multi-factor authentication (MFA) for all user account types;</li>
<li>Role-based access controls (RBAC);</li>
<li>Regular penetration testing and vulnerability assessments;</li>
<li>Web Application Firewall (WAF) and DDoS protection;</li>
<li>Automated audit logs for all data access and modification events.</li>
</ul>
<h3>8.2 Organisational Safeguards</h3>
<ul>
<li>Confidentiality agreements (NDAs) binding all employees, contractors, and onboarded Healthcare Personnel;</li>
<li>Data Processing Agreements (DPAs) with third-party technology vendors and cloud providers;</li>
<li>Background verification of Healthcare Personnel before accounts are made live;</li>
<li>Hospital registration verification before Hospital accounts are activated;</li>
<li>Designation of Grievance Officer (Dr. Indranil Chatterjee).</li>
</ul>
<h3>8.3 Breach Notification</h3>
<p>In the event of a personal data breach, MESRON HEALTHCARE LLP shall report to the Data Protection Board of India within seventy-two (72) hours of becoming aware, notify affected users without undue delay, maintain a breach register, and take immediate remedial action.</p>

<h2>9. Data Sharing and Third-Party Disclosure</h2>
<p>MESRON HEALTHCARE LLP does <strong>NOT</strong> sell, rent, trade, or share personal data with advertisers or marketing entities. Data is shared only as follows:</p>
<table>
<thead><tr><th>Recipient</th><th>Data Shared</th><th>Purpose</th><th>Basis</th></tr></thead>
<tbody>
<tr><td>Assigned Home Healthcare Personnel</td><td>Health status, health info (need-to-know)</td><td>Service delivery</td><td>Consent + Contract</td></tr>
<tr><td>Hospitals</td><td>Patient identity and health info (with consent)</td><td>Bed / Consultation Booking</td><td>Consent + Contract</td></tr>
<tr><td>Hospital Healthcare Personnel</td><td>Appointment details, relevant health info</td><td>Consultations</td><td>Consent + Contract</td></tr>
<tr><td>Payment Gateway Partners</td><td>Transaction data (no full card data stored)</td><td>Payment Processing</td><td>Contract + RBI</td></tr>
<tr><td>Cloud Provider (AWS India)</td><td>All Platform data (encrypted at rest)</td><td>Data hosting</td><td>DPA</td></tr>
<tr><td>Telecom Partner (Masked Audio)</td><td>Call metadata only — no content</td><td>Call routing</td><td>DPA</td></tr>
<tr><td>Legal / Regulatory Authorities</td><td>Required by valid legal order</td><td>Law enforcement; court orders</td><td>Legal obligation</td></tr>
<tr><td>NMC / Medical Councils</td><td>RMP credentials (not patient data)</td><td>Credential verification</td><td>Legal obligation</td></tr>
</tbody>
</table>

<h2>10. Cookies, Device Tracking and Push Notifications</h2>
<table>
<thead><tr><th>Cookie Type</th><th>Purpose</th><th>Retention</th></tr></thead>
<tbody>
<tr><td>Strictly Necessary</td><td>Session management, security, login authentication</td><td>Session only</td></tr>
<tr><td>Functional</td><td>User preferences, language, shortcuts</td><td>Up to 1 year</td></tr>
<tr><td>Analytical</td><td>Anonymised platform usage statistics</td><td>Up to 13 months</td></tr>
<tr><td>Preference</td><td>Saved health profile access shortcuts</td><td>Up to 6 months</td></tr>
</tbody>
</table>
<p>HomeCure does <strong>NOT</strong> use advertising or third-party marketing cookies. Device identifiers and GPS location are collected only with explicit user permission, used for home healthcare dispatch and SOS booking, and can be revoked via device settings.</p>

<h2>11. Grievance Redressal Mechanism</h2>
<table>
<tbody>
<tr><td><strong>Grievance Officer</strong></td><td>Dr. Indranil Chatterjee</td></tr>
<tr><td>Organisation</td><td>MESRON HEALTHCARE LLP — HomeCure</td></tr>
<tr><td>Email</td><td>admin@homecure.co.in</td></tr>
<tr><td>Phone</td><td>+91-9002296906</td></tr>
<tr><td>Hours</td><td>Monday to Saturday, 10:00 AM – 6:00 PM (IST)</td></tr>
<tr><td>Address</td><td>Premises No.159/63, Street No.2, Hindusthan Park, Hirapur, Asansol – 713304, West Bengal, India</td></tr>
</tbody>
</table>
<ul>
<li>Submit complaints in writing to admin@homecure.co.in with a clear description of the issue;</li>
<li>Acknowledgement within forty-eight (48) hours of receipt;</li>
<li>Resolution or substantive response within thirty (30) days;</li>
<li>Unsatisfied complainants may escalate to the Data Protection Board of India or courts of competent jurisdiction in West Bengal.</li>
</ul>

<h2>12. Amendments to this Privacy Policy</h2>
<p>MESRON HEALTHCARE LLP reserves the right to amend this Policy. Material changes will be notified via in-app notification and/or email at least thirty (30) days prior to taking effect.</p>

<hr/>

<h1>PART B — PRIVACY PROVISIONS FOR PATIENTS / USERS</h1>

<h2>13. Patient Data Collected and Processed</h2>
<h3>13.1 Identity and Contact</h3>
<ul>
<li>Full legal name; Date of birth and age; Gender;</li>
<li>Residential address (for home healthcare delivery);</li>
<li>Mobile phone number (primary and alternate); Email address;</li>
<li>Profile photograph (optional); Emergency contact details.</li>
</ul>
<h3>13.2 Device and Technical Data</h3>
<ul>
<li>Device type, model, OS version, unique identifiers;</li>
<li>IP address and approximate geolocation; Precise GPS (with consent);</li>
<li>App usage logs; Push notification tokens.</li>
</ul>
<h3>13.3 Booking and Transaction Data</h3>
<ul>
<li>Type of service booked; Booking date, time, and status;</li>
<li>Commission charges paid; Cancellation and refund records;</li>
<li>Rating and review submissions.</li>
</ul>

<h2>14. Health Profile and Sensitive Personal Data (SPDI)</h2>
<p>The following categories of data constitute SPDI under Rule 3 of the SPDI Rules, 2011, and are processed with the highest level of protection and only upon explicit consent:</p>
<ul>
<li>Medical history, pre-existing conditions / comorbidities (diabetes, hypertension, cardiac conditions, thyroid disorders, etc.);</li>
<li>Known allergies and adverse drug reactions;</li>
<li>Current medications and treatment history;</li>
<li>Addiction history (tobacco, alcohol, substance use — user-disclosed);</li>
<li>Surgical and hospitalisation history;</li>
<li>Physiotherapy needs; dietary requirements; vital statistics; clinical notes;</li>
<li>Prescriptions and diagnostic reports;</li>
<li>Mental health disclosures (treated with utmost confidentiality);</li>
<li>Passwords (encrypted at rest, never stored in plain text);</li>
<li>Financial information (UPI IDs, masked card details).</li>
</ul>
<p>Health data is never shared with advertisers, data brokers, or insurance companies without explicit, purpose-specific consent.</p>

<h2>15. Hospital Bed Bookings — SOS and Scheduled</h2>
<h3>15.1 SOS (Emergency) Bed Bookings</h3>
<ul>
<li>Precise GPS location is accessed in real-time (with prior consent) to identify and contact the nearest available Hospital;</li>
<li>The Patient's stored health profile is made available to the receiving Hospital upon the Patient's consent (see Section 22);</li>
<li>An automatic notification is sent to the Patient's registered emergency contact upon SOS activation;</li>
<li>SOS booking records are retained for a minimum of three (3) years;</li>
<li>A commission charge based on bed type is collected at booking. This is refunded if the Patient does not proceed with actual hospital admission.</li>
</ul>
<h3>15.2 Scheduled (Planned) Bed Bookings</h3>
<ul>
<li>A commission charge based on bed type is collected at booking confirmation, refunded if the Patient does not get admitted;</li>
<li>The final and full payment for hospital admission, treatment, procedures and services is to be made directly at the Hospital. MESRON HEALTHCARE LLP has no role, control, or liability in respect of the Hospital's final billing;</li>
<li>MESRON HEALTHCARE LLP does not at any point represent, estimate, guarantee or provide any indication of the total treatment cost or hospital bill.</li>
</ul>
<p><strong>Important Notice:</strong> The commission charge collected at bed booking is MESRON HEALTHCARE LLP's platform facilitation fee only. The actual hospital bill is entirely determined by and payable to the Hospital directly.</p>

<h2>16. Home Healthcare Services — Data Handling</h2>
<h3>16.1 Data Collected at Booking</h3>
<ul>
<li>Patient's full name, residential address, GPS coordinates (with consent);</li>
<li>Nature of service required (doctor / nursing / physiotherapy / dietician / Ayah / medical technology services);</li>
<li>Reason for visit and relevant medical history; preferred date, time slot, frequency; emergency contact.</li>
</ul>
<h3>16.2 Data Shared with Home Healthcare Personnel</h3>
<p>Upon booking confirmation, on a strict need-to-know basis: Patient's name, address, and contact number; nature of service and relevant health information; special instructions. Personnel are bound by confidentiality obligations.</p>
<h3>16.3 Video Consultations — Home Healthcare Personnel</h3>
<ul>
<li>Doctors and dieticians are available for video consultations on booking at any time;</li>
<li>Nurses and physiotherapists are available for video consultations only after they have completed at least one (1) in-person visit to the Patient;</li>
<li>Ayahs are <strong>NOT</strong> available for video consultations under any circumstances.</li>
</ul>
<h3>16.4 Post-Visit Data</h3>
<p>Clinical notes may be added to the Patient's health record with consent; service completion records are logged for billing and quality assurance; reviews may be submitted after completion.</p>

<h2>17. Telemedicine, Video Calling and Masked Audio Calling</h2>
<h3>17.1 Video Consultation (Telemedicine)</h3>
<ul>
<li>All RMPs hold valid registrations under the NMC / State Medical Council, verified before accounts are made live;</li>
<li>Video sessions use end-to-end encryption (min. AES-256 in transit). Sessions are <strong>not</strong> recorded by MESRON HEALTHCARE LLP without prior express written consent of both parties;</li>
<li>Session metadata is logged for billing, quality assurance, and dispute resolution;</li>
<li>Patients are prohibited from recording any consultation without the prior written consent of the consulting Healthcare Personnel;</li>
<li>For Hospital Healthcare Personnel consultations, full payment must be made online through the Platform before or at the time of booking. Rejected bookings are refunded within 7 working days.</li>
</ul>
<h3>17.2 Masked Audio Calling</h3>
<ul>
<li>The Patient's actual mobile number is concealed and replaced by a virtual proxy number; the Healthcare Personnel's number is similarly masked;</li>
<li>Call metadata (date, time, duration, proxy number, anonymised IDs) is retained for 90 days;</li>
<li>Audio calls are <strong>not</strong> recorded or stored as audio files by MESRON HEALTHCARE LLP;</li>
<li>In the event of a complaint involving criminal conduct, fraud or patient safety, call metadata (not content) may be disclosed to lawful authorities upon valid legal order.</li>
</ul>
<p><strong>User Advisory:</strong> MESRON HEALTHCARE LLP officials and HomeCure Healthcare Personnel will <strong>never</strong> ask for Aadhaar numbers, bank account details, OTPs, or card details over an audio or video call.</p>

<h2>18. Health Records, Prescription and Document Storage</h2>
<ul>
<li>Prescriptions, lab reports, radiology scans (X-rays, MRIs, CT scans, ultrasounds), discharge summaries, vaccination certificates;</li>
<li>All uploaded documents are encrypted using AES-256 at rest and TLS 1.3 in transit;</li>
<li>Documents are accessible only to the Patient and Healthcare Personnel explicitly authorised by the Patient;</li>
<li>Patients can delete their stored documents at any time through the Platform.</li>
</ul>

<h2>19. Health Insurance Data</h2>
<ul>
<li>Health insurance policy details are stored solely for the Patient's personal reference and convenience;</li>
<li>MESRON HEALTHCARE LLP is <strong>not</strong> a licensed insurance intermediary, broker, or aggregator;</li>
<li>Insurance data is <strong>not</strong> shared with insurance companies, hospitals, or any third party without the Patient's explicit consent.</li>
</ul>

<h2>20. Review, Rating and Feedback by Patients</h2>
<p>After a completed appointment or hospital stay, Patients may submit reviews and ratings for:</p>
<ul>
<li>Home Healthcare Personnel (doctors, nurses, physiotherapists, dieticians, ayahs, medical technologists);</li>
<li>Hospital Healthcare Personnel (doctors, physiotherapists, dieticians) for both in-person and video consultations;</li>
<li>Hospital department and the treating hospital doctor;</li>
<li>The Hospital overall after the Patient has completed a hospital stay.</li>
</ul>
<p>Reviews are submitted under the Patient's registered name. Fully anonymous reviews are not permitted to prevent misuse. Defamatory, false or abusive content will be removed.</p>

<h2>21. Payment Processing — Patients</h2>
<ul>
<li><strong>Home Healthcare Services:</strong> Full payment online through the Platform.</li>
<li><strong>Hospital Consultation (in-person and video):</strong> Full payment online through the Platform before or at booking.</li>
<li><strong>Hospital Bed Booking Commission:</strong> Commission based on bed type, refunded if not admitted.</li>
<li><strong>Refunds for Rejected Bookings:</strong> Full refund within seven (7) working days via original payment instrument.</li>
</ul>
<p>MESRON HEALTHCARE LLP does <strong>not</strong> store full card numbers, CVV codes, or UPI PINs. Financial records retained for 7 years under Companies Act, 2013 and GST.</p>

<h2>22. Consent for Sharing Patient Data with Hospitals</h2>
<p>MESRON HEALTHCARE LLP does <strong>not</strong> share Patient personal data (including health data) with Hospitals without the Patient's explicit, informed, and specific consent.</p>
<ul>
<li>A clear consent screen specifies exactly what data will be shared and for what purpose;</li>
<li>Consent is granular and revocable;</li>
<li>A consent log is maintained for all data-sharing events, accessible to the Patient through the Platform.</li>
</ul>

<h2>23. Children (Minor) Users</h2>
<p>Minors may not register independently. A Guardian must create the Minor's profile and provide all health information and consent. MESRON HEALTHCARE LLP does not subject Minor users to advertising, profiling, or tracking beyond what is strictly necessary.</p>

<h2>24. Rights of the Patient / Data Principal</h2>
<ul>
<li><strong>Right to Information (Section 11)</strong></li>
<li><strong>Right to Correction and Updation (Section 12)</strong></li>
<li><strong>Right to Erasure (Section 12 / Rule 5, SPDI Rules)</strong></li>
<li><strong>Right to Grievance Redressal (Section 13)</strong></li>
<li><strong>Right to Nominate (Section 14)</strong></li>
<li><strong>Right to Withdraw Consent (Section 7)</strong></li>
<li><strong>Right to Access (SPDI Rules, Rule 5(7))</strong></li>
</ul>

<hr/>

<h1>PART C — PRIVACY PROVISIONS FOR HOSPITALS</h1>

<h2>25. Hospital Registration and Verification Data</h2>
<p>Hospital accounts are made live only after successful verification of:</p>
<ul>
<li>Hospital name, type (private / public / charitable), legal status;</li>
<li>Hospital Registration Number under Clinical Establishments (Registration and Regulation) Act, 2010 or applicable State Act — mandatory;</li>
<li>State / district licensing authority details;</li>
<li>Authorised signatory details (PAN, contact);</li>
<li>Hospital address, GPS coordinates, contact numbers, email;</li>
<li>List of departments, bed categories, operational specialities;</li>
<li>List of empanelled doctors, physios, dieticians;</li>
<li>Banking and payment settlement details; GST registration; accreditation (NABH, JCI) if any.</li>
</ul>

<h2>26. Hospital Booking and Bed Management Data</h2>
<ul>
<li>Bed availability data updated by Hospital administrators in real-time;</li>
<li>SOS and scheduled bed booking requests received (Patient data shared only with consent);</li>
<li>Booking confirmation, rejection, cancellation records;</li>
<li>Doctor / physio / dietician calendar slots.</li>
</ul>
<h3>26.1 Booking Rejection and Refund</h3>
<p>Hospitals may reject SOS or scheduled bookings; upon rejection, the full amount paid by the Patient is refunded within 7 working days.</p>

<h2>27. Hospital Healthcare Personnel Data (Under Hospitals)</h2>
<p>Hospitals register their doctors, physiotherapists, and dieticians on the Platform with name, designation, specialisation, Medical Registration Number (MRN), consultation fees and calendar.</p>

<h2>28. Commission, Payment and Financial Data — Hospitals</h2>
<ul>
<li>MESRON HEALTHCARE LLP charges Patients a commission. Hospitals do <strong>not</strong> pay a commission to MESRON HEALTHCARE LLP;</li>
<li>Consultation fees are collected from Patients online and settled to the Hospital through the gateway integration;</li>
<li>Bed-booking commissions are HomeCure's facilitation fee and do not form part of Hospital revenue;</li>
<li>Final hospital admission bills are settled directly between Patient and Hospital. MESRON HEALTHCARE LLP has no role or liability;</li>
<li>Records retained 7 years.</li>
</ul>

<h2>29. Patient Data Shared with Hospitals — Conditions</h2>
<ul>
<li>Only with the Patient's explicit, specific, informed consent (Section 22);</li>
<li>Data shared is limited to what is strictly necessary (data minimisation);</li>
<li>Hospitals must use Patient data solely for processing the HomeCure booking; secondary use is prohibited;</li>
<li>A log of all Patient data shared with Hospitals is maintained.</li>
</ul>

<h2>30. Review and Rating Data — Hospitals</h2>
<p>Ratings are subject to MESRON HEALTHCARE LLP's content moderation policies. As an intermediary, we are not liable for the content of genuine Patient reviews.</p>

<hr/>

<h1>PART D — PRIVACY PROVISIONS FOR HEALTHCARE PERSONNEL</h1>

<h2>31. Healthcare Personnel Registration and Verification</h2>
<table>
<thead><tr><th>Category</th><th>Mandatory Registration Data</th></tr></thead>
<tbody>
<tr><td>Doctors</td><td>Name, NMC/State Medical Council Registration Number, qualification, specialisation, experience, ID, PAN, bank details</td></tr>
<tr><td>Nurses</td><td>Name, Nursing Council Registration Number, qualification, ID, PAN, bank details</td></tr>
<tr><td>Physiotherapists</td><td>Name, State Physiotherapy Council / IAP registration, qualification, ID, PAN, bank details</td></tr>
<tr><td>Dieticians</td><td>Name, IDA credentials or relevant qualification, ID, PAN, bank details</td></tr>
<tr><td>Medical Technologists</td><td>Name, DMLT / BMLT or relevant certification, lab licence (if applicable), ID, PAN, bank details</td></tr>
<tr><td>Ayahs (Attendants)</td><td>Name, government photo ID, emergency contact, training certifications (if any), background verification</td></tr>
</tbody>
</table>
<p>Accounts are made live only after successful verification by MESRON HEALTHCARE LLP.</p>

<h2>32. Home Healthcare Personnel — Service and Booking Data</h2>
<ul>
<li>Personnel may accept or reject each booking at their sole discretion;</li>
<li>On acceptance, the Personnel receives the Patient's name, address, contact number and relevant health information on a strict need-to-know basis;</li>
<li>Personnel are bound by confidentiality;</li>
<li>Payments are collected from Patients online; MESRON HEALTHCARE LLP deducts its commission and settles the net to the Personnel;</li>
<li>Rejected bookings: full refund to Patient within 7 working days;</li>
<li>Personnel performance is tracked for quality assurance.</li>
</ul>

<h2>33. Hospital-Listed Healthcare Personnel</h2>
<ul>
<li>Maintain calendar (free / occupied slots) in real-time;</li>
<li>Accept or reject in-person and video consultation bookings;</li>
<li>Comply with Telemedicine Practice Guidelines, 2020 (verification, informed consent, e-prescriptions);</li>
<li>Rejected bookings: full refund to Patient within 7 working days.</li>
</ul>

<h2>34. Video Calling and Masked Audio Calling — Healthcare Personnel</h2>
<h3>34.1 Video Consultations</h3>
<ul>
<li>Doctors and dieticians: anytime on booking;</li>
<li>Nurses and physiotherapists: only after one (1) in-person visit;</li>
<li>Ayahs are <strong>NOT</strong> permitted to conduct video consultations;</li>
<li>No recording without prior written consent of the Patient.</li>
</ul>
<h3>34.2 Masked Audio Calling</h3>
<p>Personnel's actual phone number is never disclosed to Patients. Call metadata retained 90 days. Audio content is not recorded or stored.</p>

<h2>35. Commission, Payment and Financial Data — Healthcare Personnel</h2>
<ul>
<li>Patients pay through the Platform; MESRON HEALTHCARE LLP deducts commission and settles net to Personnel;</li>
<li>PAN details collected for TDS compliance;</li>
<li>Payment settlement records retained 7 years.</li>
</ul>

<h2>36. Review and Rating Data — Healthcare Personnel</h2>
<p>Reviews reflect Patient experiences and are subject to moderation. Performance ratings affect visibility and ranking on the Platform.</p>

<hr/>

<h1>PART E — INTERMEDIARY STATUS, LIABILITY AND DISCLAIMERS</h1>

<h2>37. Intermediary Status under IT Act, 2000</h2>
<p>MESRON HEALTHCARE LLP operates the HomeCure Platform as an 'intermediary' within Section 2(1)(w) of the Information Technology Act, 2000. We do not directly provide medical, clinical, or healthcare services. Medical services are provided by independent, verified Healthcare Personnel and Hospitals listed on the Platform. We comply with due diligence requirements under the IT (Intermediary Guidelines and Digital Media Ethics Code) Rules, 2021, including appointment of a Grievance Officer, content moderation, and 72-hour takedown obligations.</p>

<h2>38. Hospital Billing Disclaimer</h2>
<p><strong>CRITICAL DISCLAIMER:</strong> MESRON HEALTHCARE LLP, through the HomeCure Platform, facilitates the booking of hospital beds and consultations and charges a platform commission. MESRON HEALTHCARE LLP does <strong>NOT</strong> at any point, in any communication, through any medium, state, estimate, indicate, guarantee or provide any representation regarding the total cost of treatment, hospitalisation, procedures, medicines, investigations, room charges, or any other component of a Hospital's bill. The final hospital bill is entirely determined by the Hospital and is payable directly to the Hospital by the Patient.</p>

<h2>39. Limitation of Liability</h2>
<ul>
<li>No system of internet data transmission or electronic storage is 100% secure;</li>
<li>MESRON HEALTHCARE LLP shall not be liable for losses arising from User's failure to protect account credentials, or from third-party actions beyond our reasonable control;</li>
<li>We are not responsible for the privacy or data practices of Hospitals, Healthcare Personnel, third-party payment gateways or external websites;</li>
<li>Maximum aggregate liability under this Privacy Policy shall not exceed the commission amount charged from the User for the specific booking that gave rise to the claim.</li>
</ul>

<h2>40. Contact Information</h2>
<table>
<tbody>
<tr><td>Legal Entity</td><td>MESRON HEALTHCARE LLP</td></tr>
<tr><td>LLPIN</td><td>ACD-8580</td></tr>
<tr><td>Brand Name</td><td>HomeCure</td></tr>
<tr><td>Registered Office</td><td>Premises No.159/63, Street No.2, Hindusthan Park, Hirapur, Asansol – 713304, West Bengal, India</td></tr>
<tr><td>Website</td><td>www.homecure.co.in</td></tr>
<tr><td>Email</td><td>admin@homecure.co.in</td></tr>
<tr><td>Phone / WhatsApp</td><td>+91-9002296906</td></tr>
<tr><td>Grievance Officer</td><td>Dr. Indranil Chatterjee</td></tr>
<tr><td>Governing Jurisdiction</td><td>Courts of West Bengal, India</td></tr>
<tr><td>Governing Law</td><td>Laws of the Republic of India</td></tr>
</tbody>
</table>

<p><em>Declaration: This Privacy Policy has been drafted in good faith to comply with the Digital Personal Data Protection Act, 2023, the DPDP Rules, 2025, the IT Act, 2000, the SPDI Rules, 2011, the Telemedicine Practice Guidelines, 2020, and all other applicable laws of India.</em></p>
`;
